"use client";

import { useCallback, useEffect, useState } from "react";
import { formatResetIn, type UsageSummary } from "@/lib/usage-limits";
import type { Plan } from "@/lib/feature-access";

interface PaymentMethod {
    brand: string;
    last4: string;
    expMonth: number | null;
    expYear: number | null;
}

interface Transaction {
    id: string;
    number: string | null;
    description: string;
    amount: number;
    currency: string;
    status: string;
    created: string;
    hostedInvoiceUrl: string | null;
    pdfUrl: string | null;
}

interface Upcoming {
    amount: number;
    currency: string;
    date: string | null;
}

interface BillingData {
    plan: Plan;
    effectivePlan: Plan;
    school: { name: string; role: "student" | "supervisor" } | null;
    interval: string | null;
    status: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    referralBonusUntil: string | null;
    usage: UsageSummary;
    paymentMethod: PaymentMethod | null;
    upcoming: Upcoming | null;
    transactions: Transaction[];
    portalAvailable: boolean;
    stripeError: boolean;
}

const STATUS_LABELS: Record<string, string> = {
    active: "Active",
    trialing: "Trial",
    past_due: "Past due",
    canceled: "Canceled",
    incomplete: "Incomplete",
    incomplete_expired: "Expired",
    unpaid: "Unpaid",
    paused: "Paused",
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
    paid: "Paid",
    open: "Due",
    uncollectible: "Failed",
    void: "Void",
};

function planName(plan: Plan): string {
    if (plan === "plus") return "Plus";
    if (plan === "pro") return "Pro";
    return "Free";
}

function formatMoney(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency.toUpperCase(),
        }).format(amount / 100);
    } catch {
        return `$${(amount / 100).toFixed(2)}`;
    }
}

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function formatExpiry(method: PaymentMethod): string | null {
    if (!method.expMonth || !method.expYear) return null;
    return `${String(method.expMonth).padStart(2, "0")}/${String(method.expYear).slice(-2)}`;
}

export function BillingPanel() {
    const [data, setData] = useState<BillingData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const [portalError, setPortalError] = useState<string | null>(null);
    const [now, setNow] = useState(() => new Date());

    const load = useCallback(async () => {
        try {
            const res = await fetch("/api/billing");
            if (!res.ok) {
                setError("We couldn't load your billing details.");
                return;
            }
            setData((await res.json()) as BillingData);
            setError(null);
        } catch {
            setError("We couldn't load your billing details.");
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), 30_000);
        return () => window.clearInterval(id);
    }, []);

    const openPortal = async () => {
        setPortalLoading(true);
        setPortalError(null);
        try {
            const res = await fetch("/api/stripe/portal", { method: "POST" });
            const payload = await res.json();
            if (!res.ok || !payload?.url) {
                setPortalError(payload?.error ?? "Couldn't open the billing portal.");
                return;
            }
            window.location.href = payload.url;
        } catch {
            setPortalError("Couldn't open the billing portal.");
        } finally {
            setPortalLoading(false);
        }
    };

    if (error) {
        return (
            <div className="bill-panel">
                <div className="bill-empty bill-empty--error">
                    <p>{error}</p>
                    <button type="button" className="bill-btn" onClick={() => void load()}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="bill-panel">
                <div className="bill-skeleton bill-skeleton--card" />
                <div className="bill-skeleton bill-skeleton--card" />
                <div className="bill-skeleton bill-skeleton--rows" />
            </div>
        );
    }

    const active = data.plan !== "free";
    const referralActive = Boolean(
        data.referralBonusUntil && new Date(data.referralBonusUntil) > now
    );
    const school = data.school ?? null;
    const schoolActive = school !== null && !active && !referralActive;
    const shownPlan = data.effectivePlan;
    const statusLabel = data.status
        ? STATUS_LABELS[data.status] ?? data.status.replace(/_/g, " ")
        : null;
    const renewalLabel = data.cancelAtPeriodEnd || data.status === "canceled"
        ? "Access ends"
        : "Renews";

    const usage = data.usage;
    const usedSlots = usage.limit ? Math.min(usage.used, usage.limit) : usage.used;
    const slots = usage.limit ?? 0;
    const exhausted = usage.limited && (usage.remaining ?? 0) <= 0;

    return (
        <div className="bill-panel">
            <section className={`bill-plan bill-plan--${shownPlan}`}>
                <div className="bill-plan__head">
                    <div>
                        <div className="bill-plan__eyebrow">Current plan</div>
                        <div className="bill-plan__name">
                            Signpost {planName(shownPlan)}
                            {statusLabel && active && (
                                <span
                                    className={`bill-status bill-status--${data.status ?? "active"}`}
                                >
                                    {statusLabel}
                                </span>
                            )}
                            {referralActive && !active && (
                                <span className="bill-status bill-status--bonus">
                                    Referral bonus
                                </span>
                            )}
                            {schoolActive && (
                                <span className="bill-status bill-status--school">School</span>
                            )}
                        </div>
                        <div className="bill-plan__meta">
                            {active && data.interval
                                ? `Billed ${data.interval}`
                                : referralActive
                                  ? `Free Pro access from referrals`
                                  : schoolActive
                                    ? `Included with ${school.name}`
                                    : "No subscription — you're on the free plan"}
                        </div>
                    </div>
                    {data.upcoming && (
                        <div className="bill-plan__amount">
                            <span className="bill-plan__amount-value">
                                {formatMoney(data.upcoming.amount, data.upcoming.currency)}
                            </span>
                            <span className="bill-plan__amount-label">next payment</span>
                        </div>
                    )}
                </div>

                <div className="bill-plan__rows">
                    {(active || referralActive) && (
                        <div className="bill-row">
                            <span className="bill-row__label">{renewalLabel}</span>
                            <span className="bill-row__value">
                                {formatDate(
                                    active ? data.currentPeriodEnd : data.referralBonusUntil
                                )}
                            </span>
                        </div>
                    )}
                    <div className="bill-row">
                        <span className="bill-row__label">Unit limit</span>
                        <span className="bill-row__value">
                            {usage.limited
                                ? `${usage.limit} units / ${usage.windowHours}h`
                                : "Unlimited"}
                        </span>
                    </div>
                </div>

                <div className="bill-plan__actions">
                    <a className="bill-btn bill-btn--primary" href="/plans">
                        {active ? "Change plan" : "Upgrade"}
                    </a>
                    {data.portalAvailable && (
                        <button
                            type="button"
                            className="bill-btn"
                            onClick={openPortal}
                            disabled={portalLoading}
                        >
                            {portalLoading ? "Opening…" : "Manage subscription"}
                        </button>
                    )}
                </div>
                {portalError && <div className="bill-inline-error">{portalError}</div>}
            </section>

            <section className="bill-card">
                <div className="bill-card__head">
                    <h3 className="bill-card__title">Usage</h3>
                    <span className="bill-card__hint">
                        Rolling {usage.windowHours}-hour window
                    </span>
                </div>

                {usage.limited ? (
                    <>
                        <div className="bill-usage__figures">
                            <span className="bill-usage__count">
                                {usedSlots}
                                <span className="bill-usage__total">/{usage.limit}</span>
                            </span>
                            <span className="bill-usage__label">
                                units started in the last {usage.windowHours} hours
                            </span>
                        </div>
                        <div className="bill-usage__meter" role="presentation">
                            {Array.from({ length: slots }, (_, i) => (
                                <span
                                    key={i}
                                    className={`bill-usage__slot ${
                                        i < usedSlots ? "bill-usage__slot--filled" : ""
                                    }`}
                                />
                            ))}
                        </div>
                        <p className="bill-usage__note">
                            {exhausted
                                ? `Limit reached. A new unit unlocks in ${formatResetIn(usage.resetAt, now)}.`
                                : usage.resetAt
                                  ? `${usage.remaining} left. Your oldest unit frees up in ${formatResetIn(usage.resetAt, now)}.`
                                  : `${usage.remaining} units available. Lessons inside a unit you've already started don't count again.`}
                        </p>
                        <a className="bill-usage__cta" href="/plans">
                            Upgrade for unlimited units
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="13 5 20 12 13 19" />
                            </svg>
                        </a>
                    </>
                ) : (
                    <div className="bill-usage__unlimited">
                        <div className="bill-usage__unlimited-glyph" aria-hidden="true">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 12c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4 4 1.8 4 4Z" />
                                <path d="M12 12c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4Z" />
                            </svg>
                        </div>
                        <div>
                            <div className="bill-usage__unlimited-title">Unlimited units</div>
                            <div className="bill-usage__unlimited-sub">
                                {usage.used > 0
                                    ? `${usage.used} started in the last ${usage.windowHours} hours`
                                    : "No cap on how much you practice."}
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <section className="bill-card">
                <div className="bill-card__head">
                    <h3 className="bill-card__title">Payment method</h3>
                </div>
                {data.paymentMethod ? (
                    <div className="bill-method">
                        <span className="bill-method__brand">{data.paymentMethod.brand}</span>
                        <span className="bill-method__digits">
                            <span aria-hidden="true">•••• •••• ••••</span>{" "}
                            {data.paymentMethod.last4}
                        </span>
                        {formatExpiry(data.paymentMethod) && (
                            <span className="bill-method__exp">
                                Expires {formatExpiry(data.paymentMethod)}
                            </span>
                        )}
                        {data.portalAvailable && (
                            <button
                                type="button"
                                className="bill-method__update"
                                onClick={openPortal}
                                disabled={portalLoading}
                            >
                                Update
                            </button>
                        )}
                    </div>
                ) : (
                    <p className="bill-empty__text">
                        No card on file. One gets saved the first time you subscribe.
                    </p>
                )}
            </section>

            <section className="bill-card">
                <div className="bill-card__head">
                    <h3 className="bill-card__title">Transactions</h3>
                    {data.transactions.length > 0 && (
                        <span className="bill-card__hint">
                            {data.transactions.length} most recent
                        </span>
                    )}
                </div>

                {data.stripeError ? (
                    <p className="bill-empty__text">
                        Stripe didn't respond just now. Reload to try again.
                    </p>
                ) : data.transactions.length === 0 ? (
                    <p className="bill-empty__text">
                        No charges yet. Invoices show up here after your first payment.
                    </p>
                ) : (
                    <ul className="bill-txns">
                        {data.transactions.map((txn) => (
                            <li key={txn.id} className="bill-txn">
                                <div className="bill-txn__main">
                                    <span className="bill-txn__desc">{txn.description}</span>
                                    <span className="bill-txn__date">
                                        {formatDate(txn.created)}
                                        {txn.number ? ` · ${txn.number}` : ""}
                                    </span>
                                </div>
                                <span
                                    className={`bill-txn__status bill-txn__status--${txn.status}`}
                                >
                                    {INVOICE_STATUS_LABELS[txn.status] ?? txn.status}
                                </span>
                                <span className="bill-txn__amount">
                                    {formatMoney(txn.amount, txn.currency)}
                                </span>
                                {txn.hostedInvoiceUrl || txn.pdfUrl ? (
                                    <a
                                        className="bill-txn__link"
                                        href={(txn.hostedInvoiceUrl ?? txn.pdfUrl) as string}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={`View invoice from ${formatDate(txn.created)}`}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M15 3h6v6" />
                                            <path d="M10 14 21 3" />
                                            <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                                        </svg>
                                    </a>
                                ) : (
                                    <span className="bill-txn__link bill-txn__link--empty" />
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
